import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, Database, MapPin, User, Package, Sparkles, CheckCircle, AlertCircle, Loader, Shield, AlertTriangle, Eye, EyeOff, Layers, Info, X } from 'lucide-react';
import { getAgentKnowledgeStatus, isTerminalSyncStatus, syncAgent, SyncProgress } from '../../api/client';

interface AgentDetailsDialogProps {
  accountId?: string | null;
  workspaceId?: string | null;
  workspaceName?: string | null;
  agentSlug?: string | null;
  agentName?: string | null;
}

interface QCStatus {
  enabled: boolean;
  trained: boolean;
  available: boolean;
  schema_version?: string;
  rule_count?: number;
  categories?: Record<string, number>;
  severities?: Record<string, number>;
  verdicts?: Record<string, number>;
  scenes?: Record<string, number>;
  mandatory_count?: number;
  coverage?: {
    with_visual_cues: number;
    with_anti_cues: number;
    with_scenes: number;
  };
  source?: string;
  project_name?: string;
  reason?: string;
}

interface KnowledgeStatus {
  agent: string;
  local: {
    total_entities: number;
    categories: Record<string, number>;
    synced: boolean;
  };
  remote: {
    entity_count?: number;
    has_config?: boolean;
    scopes?: string[];
    trained_scopes?: string[];
    last_updated?: string;
    status?: string;
  };
  qc?: QCStatus | null;
  last_synced: string | null;
}

/**
 * Single source of truth for the knowledge sync section.
 *
 * `last_synced` is a real sync-completion timestamp (set by the backend only
 * after a sync run finishes), while the "Not synced yet" prompt was driven by
 * a separate condition — whether the Knowledge Graph left any entities behind.
 * Rendering those two independently let the dialog claim "Not synced yet" and
 * "Last synced: <time>" at once. Deriving one discriminated state makes that
 * combination unrepresentable.
 */
type KnowledgeSyncView =
  | { kind: 'never-synced' }
  | { kind: 'synced'; syncedAt: string | null }
  | { kind: 'synced-empty'; syncedAt: string };

const deriveSyncView = (
  local: KnowledgeStatus['local'],
  lastSynced: string | null
): KnowledgeSyncView => {
  if (local.synced && local.total_entities > 0) {
    // Knowledge is present. `syncedAt` may still be null when the entities
    // came from an earlier run: the backend keeps sync times in memory, so a
    // restart loses the timestamp without losing the ingested data.
    return { kind: 'synced', syncedAt: lastSynced };
  }
  if (lastSynced) {
    // A sync did run, it just returned no Knowledge Graph entities.
    return { kind: 'synced-empty', syncedAt: lastSynced };
  }
  return { kind: 'never-synced' };
};

const categoryIcons: Record<string, React.ReactNode> = {
  Character: <User size={12} />,
  Location: <MapPin size={12} />,
  Object: <Package size={12} />,
  Theme: <Sparkles size={12} />,
};

const AgentDetailsDialog: React.FC<AgentDetailsDialogProps> = ({
  accountId,
  workspaceId,
  workspaceName,
  agentSlug,
  agentName,
}) => {
  const [open, setOpen] = useState(false);
  const [knowledgeStatus, setKnowledgeStatus] = useState<KnowledgeStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncNotice, setSyncNotice] = useState<string | null>(null);
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const [syncResult, setSyncResult] = useState<any>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(false);
    setSyncError(null);
    setSyncNotice(null);
    setSyncProgress(null);
    setSyncResult(null);
    setStatusError(null);

    if (!agentSlug || !accountId || !workspaceId) {
      setKnowledgeStatus(null);
      return;
    }
    setKnowledgeStatus(null);
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const status = await getAgentKnowledgeStatus(agentSlug, accountId, workspaceId, workspaceName || undefined);
        if (!cancelled) setKnowledgeStatus(status);
      } catch (error) {
        console.error('Error fetching knowledge status:', error);
        if (!cancelled) {
          setStatusError(error instanceof Error ? error.message : 'Failed to fetch knowledge status');
        }
      }
    };
    fetchStatus();
    return () => {
      cancelled = true;
    };
  }, [agentSlug, accountId, workspaceId, workspaceName]);

  useEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    closeButtonRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      trigger?.focus();
    };
  }, [open]);

  const handleSync = async () => {
    if (!agentSlug || !accountId || !workspaceId || syncing) return;
    setSyncing(true);
    setSyncError(null);
    setSyncNotice(null);
    setSyncProgress(null);
    setSyncResult(null);
    try {
      const result = await syncAgent(agentSlug, accountId, workspaceId, true, workspaceName || undefined, {
        onProgress: setSyncProgress,
      });
      setSyncResult(result);

      // Knowledge graph ingestion commits only at the very end, so refreshing
      // the knowledge status while the sync is still running reports the
      // pre-sync (empty) counts — the "spins, then reverts" report in #170.
      if (!isTerminalSyncStatus(result?.status)) {
        setSyncNotice('Sync is still running in the background — check back shortly.');
        return;
      }

      // Check for per-artifact errors (including partially ingested artifacts)
      const artifacts = result?.artifacts || {};
      const errors = Object.entries(artifacts)
        .filter(([, v]: [string, any]) => v?.status === 'error' || v?.status === 'partial')
        .map(([k, v]: [string, any]) => `${k}: ${v.error || v.status}`)
        .join('; ');
      if (errors) {
        setSyncError(errors);
      } else if (result?.status === 'completed_with_errors') {
        setSyncError('Sync completed with errors');
      } else if (result?.status === 'error') {
        setSyncError(result.error || 'Sync failed');
      }
      const status = await getAgentKnowledgeStatus(agentSlug, accountId, workspaceId, workspaceName || undefined);
      setKnowledgeStatus(status);
    } catch (error: any) {
      console.error('Sync failed:', error);
      setSyncError(error?.message || 'Sync failed');
    } finally {
      setSyncing(false);
      setSyncProgress(null);
    }
  };

  const syncButtonLabel = () => {
    if (!syncing) return 'Sync Knowledge';
    const { processed, total } = syncProgress || {};
    if (typeof processed === 'number' && typeof total === 'number' && total > 0) {
      return `Syncing ${processed.toLocaleString()} / ${total.toLocaleString()}…`;
    }
    return 'Syncing…';
  };

  const renderKnowledgePanel = () => {
    if (statusError) {
      return (
        <div className="agent-details-error" role="alert">
          <AlertCircle size={14} />
          <div>
            <strong>Knowledge status unavailable</strong>
            <div>{statusError}</div>
          </div>
        </div>
      );
    }

    if (!knowledgeStatus) {
      return (
        <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#1a1a1e', borderRadius: '8px', border: '1px solid #2e2e2e', textAlign: 'center', color: '#6b7280', fontSize: '0.75rem' }}>
          <Loader size={14} style={{ animation: 'spin 1s linear infinite', margin: '0 auto 0.25rem' }} />
          Loading knowledge status…
        </div>
      );
    }

    const { local, remote, last_synced } = knowledgeStatus;
    const syncView = deriveSyncView(local, last_synced);
    const syncedAt = syncView.kind === 'never-synced' ? null : syncView.syncedAt;

    return (
      <div style={{ marginTop: '0.5rem' }}>
        {/* Agent info card */}
        <div style={{ padding: '0.75rem', background: '#1a1a1e', borderRadius: '8px', border: '1px solid #2e2e2e' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ color: '#8b5cf6', fontWeight: 600, fontSize: '0.875rem' }}>{agentName || agentSlug}</div>
            {remote.status && (
              <span style={{
                fontSize: '0.625rem',
                padding: '0.125rem 0.375rem',
                borderRadius: '4px',
                background: remote.status === 'Indexed' ? 'rgba(34,197,94,0.15)' : 'rgba(234,179,8,0.15)',
                color: remote.status === 'Indexed' ? '#22c55e' : '#eab308',
                fontWeight: 500,
              }}>
                {remote.status}
              </span>
            )}
          </div>
          {remote.last_updated && (
            <div style={{ color: '#6b7280', fontSize: '0.625rem', marginTop: '0.25rem' }}>
              Updated: {remote.last_updated}
            </div>
          )}
        </div>

        {/* Entity breakdown */}
        {syncView.kind === 'synced' ? (
          <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#1a1a1e', borderRadius: '8px', border: '1px solid #2e2e2e' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.5rem' }}>
              <Database size={12} color="#8b5cf6" />
              <span style={{ color: '#d1d1d6', fontSize: '0.75rem', fontWeight: 600 }}>
                Knowledge Graph
              </span>
              <span style={{ color: '#6b7280', fontSize: '0.625rem', marginLeft: 'auto' }}>
                {local.total_entities} entities
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {Object.entries(local.categories)
                .sort(([, a], [, b]) => b - a)
                .map(([category, count]) => (
                  <div key={category} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.25rem 0.5rem', background: '#121212', borderRadius: '4px' }}>
                    <span style={{ color: '#8b5cf6', display: 'flex', alignItems: 'center' }}>
                      {categoryIcons[category] || <Database size={12} />}
                    </span>
                    <span style={{ color: '#9ca3af', fontSize: '0.75rem', flex: 1 }}>{category}</span>
                    <span style={{ color: '#6b7280', fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>{count}</span>
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#1a1a1e', borderRadius: '8px', border: '1px solid #2e2e2e', textAlign: 'center' }}>
            <AlertCircle size={14} color="#eab308" style={{ margin: '0 auto 0.25rem' }} />
            <div style={{ color: '#eab308', fontSize: '0.75rem', fontWeight: 500 }}>
              {syncView.kind === 'never-synced' ? 'Not synced yet' : 'Last sync found no knowledge'}
            </div>
            <div style={{ color: '#6b7280', fontSize: '0.625rem', marginTop: '0.125rem' }}>
              {syncView.kind === 'never-synced'
                ? (remote.entity_count ? `${remote.entity_count} entities available` : 'Click sync to pull knowledge')
                : (remote.entity_count ? `${remote.entity_count} entities available remotely — try syncing again` : 'Sync completed without importing any entities')}
            </div>
          </div>
        )}

        {/* Artifact availability */}
        {remote.scopes && remote.scopes.length > 0 && (
          <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', background: '#1a1a1e', borderRadius: '8px', border: '1px solid #2e2e2e' }}>
            <div style={{ color: '#6b7280', fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Scopes</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
              {remote.scopes.map((scope) => (
                <span key={scope} style={{ fontSize: '0.625rem', padding: '0.125rem 0.375rem', borderRadius: '4px', background: 'rgba(139,92,246,0.15)', color: '#a78bfa' }}>
                  {scope.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Sync button & status */}
        <div style={{ marginTop: '0.5rem' }}>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="aw-btn-secondary aw-btn-sm"
            style={{
              width: '100%',
              gap: '0.375rem',
            }}
          >
            <RefreshCw size={12} style={syncing ? { animation: 'spin 1s linear infinite' } : {}} />
            {syncButtonLabel()}
          </button>
          {syncing && syncProgress?.total ? (
            <div style={{ marginTop: '0.375rem' }}>
              <div style={{ height: '3px', background: '#2e2e2e', borderRadius: '2px', overflow: 'hidden' }}>
                <div
                  data-testid="sync-progress-bar"
                  style={{
                    height: '100%',
                    width: `${Math.min(100, Math.round(((syncProgress.processed || 0) / syncProgress.total) * 100))}%`,
                    background: '#3b82f6',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
            </div>
          ) : null}
          {syncNotice && (
            <div style={{
              color: '#eab308', fontSize: '0.625rem', marginTop: '0.375rem',
              padding: '0.375rem 0.5rem', background: 'rgba(234,179,8,0.1)',
              borderRadius: '4px', border: '1px solid rgba(234,179,8,0.2)',
              wordBreak: 'break-word', lineHeight: 1.4
            }}>
              ⏳ {syncNotice}
            </div>
          )}
          {syncError && (
            <div style={{
              color: '#ef4444', fontSize: '0.625rem', marginTop: '0.375rem',
              padding: '0.375rem 0.5rem', background: 'rgba(239,68,68,0.1)',
              borderRadius: '4px', border: '1px solid rgba(239,68,68,0.2)',
              wordBreak: 'break-word', lineHeight: 1.4
            }}>
              ⚠️ {syncError}
            </div>
          )}
          {!syncError && syncResult?.artifacts && (
            <div style={{ marginTop: '0.375rem', display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
              {Object.entries(syncResult.artifacts).map(([key, val]: [string, any]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.5625rem', color: val.status === 'ok' ? '#22c55e' : val.status === 'skipped' ? '#eab308' : '#ef4444' }}>
                  <span>{val.status === 'ok' ? '✓' : val.status === 'skipped' ? '⊘' : '✗'}</span>
                  <span style={{ color: '#9ca3af' }}>{key.replace(/_/g, ' ')}</span>
                  {val.stats?.purged > 0 && <span style={{ color: '#6b7280' }}>({val.stats.purged} purged)</span>}
                </div>
              ))}
            </div>
          )}
          {syncedAt && (
            <div style={{ color: '#4b5563', fontSize: '0.5625rem', textAlign: 'center', marginTop: '0.25rem' }}>
              <CheckCircle size={9} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.2rem' }} />
              Last synced: {new Date(syncedAt).toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderQCPanel = () => {
    const qc = knowledgeStatus?.qc;
    if (!qc || !qc.enabled) {
      // QC not in agent scopes -> render nothing (agent doesn't do QC).
      return null;
    }

    // Enabled but no envelope available (fetch failed or 404). Show a
    // muted card so the operator knows QC is a declared skill but not
    // usable yet -- more actionable than silence.
    if (!qc.available) {
      return (
        <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#1a1a1e', borderRadius: '8px', border: '1px solid #2e2e2e' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
            <Shield size={12} color="#8b5cf6" />
            <span style={{ color: '#d1d1d6', fontSize: '0.75rem', fontWeight: 600 }}>
              Quality Control
            </span>
            {!qc.trained && (
              <span style={{ fontSize: '0.5625rem', padding: '0.125rem 0.375rem', borderRadius: '4px', background: 'rgba(234,179,8,0.15)', color: '#eab308', marginLeft: 'auto' }}>
                not trained
              </span>
            )}
          </div>
          <div style={{ color: '#6b7280', fontSize: '0.6875rem' }}>
            Rules not available{qc.reason ? ` (${qc.reason.replace(/_/g, ' ')})` : ''}
          </div>
        </div>
      );
    }

    const totalRules = qc.rule_count ?? 0;
    const cov = qc.coverage || { with_visual_cues: 0, with_anti_cues: 0, with_scenes: 0 };
    const severityColour = (sev: string) => (
      sev === 'critical' ? '#ef4444' : sev === 'major' ? '#f97316' : '#eab308'
    );
    const verdictColour = (v: string) => (
      v === 'fail' ? '#ef4444' : v === 'review' ? '#eab308' : '#22c55e'
    );

    return (
      <div style={{ marginTop: '0.75rem' }}>
        {/* Header card */}
        <div style={{ padding: '0.75rem', background: '#1a1a1e', borderRadius: '8px', border: '1px solid #2e2e2e' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <Shield size={12} color="#8b5cf6" />
            <span style={{ color: '#d1d1d6', fontSize: '0.75rem', fontWeight: 600 }}>
              Quality Control
            </span>
            <span style={{ color: '#6b7280', fontSize: '0.625rem', marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>
              {totalRules} {totalRules === 1 ? 'rule' : 'rules'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.375rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.5625rem', padding: '0.0625rem 0.375rem', borderRadius: '4px', background: qc.trained ? 'rgba(34,197,94,0.15)' : 'rgba(234,179,8,0.15)', color: qc.trained ? '#22c55e' : '#eab308' }}>
              {qc.trained ? 'trained' : 'declared'}
            </span>
            {qc.schema_version && qc.schema_version !== 'legacy' && (
              <span style={{ fontSize: '0.5625rem', padding: '0.0625rem 0.375rem', borderRadius: '4px', background: 'rgba(139,92,246,0.15)', color: '#a78bfa' }}>
                {qc.schema_version}
              </span>
            )}
            {(qc.mandatory_count ?? 0) > 0 && (
              <span style={{ fontSize: '0.5625rem', padding: '0.0625rem 0.375rem', borderRadius: '4px', background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>
                {qc.mandatory_count} mandatory
              </span>
            )}
          </div>
        </div>

        {/* Category breakdown */}
        {qc.categories && Object.keys(qc.categories).length > 0 && (
          <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#1a1a1e', borderRadius: '8px', border: '1px solid #2e2e2e' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
              <Layers size={12} color="#8b5cf6" />
              <span style={{ color: '#d1d1d6', fontSize: '0.75rem', fontWeight: 600 }}>
                Rule Categories
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {Object.entries(qc.categories)
                .sort(([, a], [, b]) => b - a)
                .map(([cat, count]) => (
                  <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.25rem 0.5rem', background: '#121212', borderRadius: '4px' }}>
                    <span style={{ color: '#9ca3af', fontSize: '0.75rem', flex: 1, textTransform: 'capitalize' }}>{cat.replace(/_/g, ' ')}</span>
                    <span style={{ color: '#6b7280', fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>{count}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Severity + Verdict breakdown (side by side, compact) */}
        {(qc.severities || qc.verdicts) && (
          <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#1a1a1e', borderRadius: '8px', border: '1px solid #2e2e2e' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
              <AlertTriangle size={12} color="#8b5cf6" />
              <span style={{ color: '#d1d1d6', fontSize: '0.75rem', fontWeight: 600 }}>
                Severity / Verdict
              </span>
            </div>
            {qc.severities && Object.keys(qc.severities).length > 0 && (
              <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                {Object.entries(qc.severities).sort(([, a], [, b]) => b - a).map(([sev, count]) => (
                  <span key={sev} style={{ fontSize: '0.625rem', padding: '0.125rem 0.375rem', borderRadius: '4px', background: `${severityColour(sev)}22`, color: severityColour(sev), textTransform: 'capitalize' }}>
                    {sev} {count}
                  </span>
                ))}
              </div>
            )}
            {qc.verdicts && Object.keys(qc.verdicts).length > 0 && (
              <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                {Object.entries(qc.verdicts).sort(([, a], [, b]) => b - a).map(([v, count]) => (
                  <span key={v} style={{ fontSize: '0.625rem', padding: '0.125rem 0.375rem', borderRadius: '4px', background: `${verdictColour(v)}22`, color: verdictColour(v), textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {v} {count}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Enrichment coverage — surfaces the "quality of the ruleset" */}
        {totalRules > 0 && (
          <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', background: '#1a1a1e', borderRadius: '8px', border: '1px solid #2e2e2e' }}>
            <div style={{ color: '#6b7280', fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.375rem' }}>
              Enrichment Coverage
            </div>
            {[
              { key: 'with_visual_cues', label: 'Visual cues', icon: <Eye size={10} /> },
              { key: 'with_anti_cues', label: 'Anti-cues', icon: <EyeOff size={10} /> },
              { key: 'with_scenes', label: 'Scene gate', icon: <Sparkles size={10} /> },
            ].map(({ key, label, icon }) => {
              const n = (cov as Record<string, number>)[key] ?? 0;
              const pct = totalRules > 0 ? Math.round((n / totalRules) * 100) : 0;
              const good = pct >= 90;
              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.6875rem', color: '#9ca3af', marginBottom: '0.1875rem' }}>
                  <span style={{ color: good ? '#22c55e' : '#eab308', display: 'flex' }}>{icon}</span>
                  <span style={{ flex: 1 }}>{label}</span>
                  <span style={{ color: good ? '#22c55e' : '#eab308', fontVariantNumeric: 'tabular-nums' }}>{n}/{totalRules}</span>
                  <span style={{ color: '#6b7280', fontVariantNumeric: 'tabular-nums', minWidth: '2.5em', textAlign: 'right' }}>{pct}%</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (!agentSlug) return null;

  const warningText = syncError
    ? 'Last knowledge sync failed'
    : knowledgeStatus && !knowledgeStatus.local.synced
      ? 'Knowledge has not been synced'
      : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="agent-details-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Agent details for ${agentName || agentSlug}`}
        onClick={() => setOpen(true)}
      >
        <Info size={15} aria-hidden="true" />
        <span>Agent details</span>
        {warningText && (
          <span
            className="agent-details-warning"
            role="status"
            aria-label={warningText}
            title={warningText}
          >
            <AlertTriangle size={13} aria-hidden="true" />
          </span>
        )}
      </button>

      {open && (
        <div
          className="agent-details-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setOpen(false);
          }}
        >
          <div
            ref={dialogRef}
            className="agent-details-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="agent-details-title"
          >
            <div className="agent-details-dialog-header">
              <div>
                <h2 id="agent-details-title">Agent details</h2>
                <p>{agentName || agentSlug}</p>
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                className="agent-details-close"
                aria-label="Close agent details"
                onClick={() => setOpen(false)}
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            <div className="agent-details-dialog-body">
              {renderKnowledgePanel()}
              {renderQCPanel()}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AgentDetailsDialog;
